import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Play, Clock, Video, Loader2 } from 'lucide-react';
import { playlistsAPI } from '../services/api';
import TeamGate from '../components/TeamGate';
import { useNavigate } from 'react-router-dom';
import CreatePlaylistModal from '../components/CreatePlaylistModal';

const Playlists = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const navigate = useNavigate();

  const fetchPlaylists = async () => {
    try {
      const response = await playlistsAPI.list();
      setPlaylists(response.data);
    } catch (error) {
      console.error('Failed to fetch playlists:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <TeamGate title="Playlists">
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Playlists</h1>
            <p className="text-gray-400">Organize and share your curated clips</p>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Playlist
          </Button>
        </div>

        <CreatePlaylistModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setLoading(true);
            fetchPlaylists();
          }}
        />

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {playlists.map((playlist) => (
              <Card
                key={playlist.id}
                className="bg-[#1a1a1b] border-gray-800 hover:border-gray-600 cursor-pointer transition-all group"
                onClick={() => navigate(`/playlists/${playlist.id}`)}
              >
                <CardHeader>
                  <CardTitle className="text-white text-lg group-hover:text-blue-400 transition-colors">
                    {playlist.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400 flex items-center gap-1">
                        <Video className="w-4 h-4" />
                        {playlist.video_count || 0} clips
                      </span>
                      <span className="text-gray-400 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {/* Duration calculation would happen on backend likely, or sum of videos */}
                        {formatDuration(0)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Created • {new Date(playlist.created_at).toLocaleDateString()}
                    </div>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-2">
                      <Play className="w-4 h-4 mr-2" />
                      Play Playlist
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && playlists.length === 0 && (
          <div className="text-center py-16">
            <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No playlists found</p>
          </div>
        )}
      </div>
    </TeamGate>
  );
};

export default Playlists;
